import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useSeo from '@/hooks/useSeo';
import { testimonials, categories } from '@/mocks/siteData';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '先核实，再发布',
    text: '每条线索至少经过两处公开信源交叉比对，无法确认的部分会明确标注为“待核实”，不做定论式表达。',
  },
  {
    icon: 'ri-user-unfollow-line',
    title: '统一使用代称',
    text: '涉及具体个人时统一使用“某艺人”“百万粉博主”这类代称，避免对当事人造成不必要的困扰。',
  },
  {
    icon: 'ri-links-line',
    title: '标注来源与时间',
    text: '每篇文章顶部都会写明更新时间，时间线按节点排列，方便你快速判断信息的新鲜程度。',
  },
  {
    icon: 'ri-emotion-happy-line',
    title: '围观但不越界',
    text: '我们讨论公开信息，不鼓励窥探隐私，也不参与任何形式的人身攻击与站队式起哄。',
  },
];

const milestones = [
  { year: '2023', text: '以整理娱乐热点时间线为起点，开始更新第一条爆料进展。' },
  { year: '2024', text: '上线话题分类与进展时间线，形成可回溯的内容结构。' },
  { year: '2025', text: '开放线索征集入口，读者可直接提供现场信息。' },
  { year: '2026', text: '完善核实流程与代称规范，内容更新覆盖每日主要热点。' },
];

export default function AboutPage() {
  const crumbs = [{ label: '首页', to: '/' }, { label: '关于我们' }];

  useSeo({
    title: '关于我们 - 爆料吃瓜内容原则与团队定位',
    description:
      '了解爆料吃瓜的品牌定位、发展理念与内容原则。我们坚持先核实再发布、人物统一使用代称，把零散线索整理成可回溯的时间线。',
    keywords: '爆料吃瓜,关于我们,内容原则,爆料吃瓜官网',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们 - 爆料吃瓜',
        inLanguage: 'zh-CN',
        url: 'https://rudolf1922.cn/about',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: crumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.label,
          item: crumb.to ? `https://rudolf1922.cn${crumb.to}` : undefined,
        })),
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={crumbs} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-5xl">
            关于爆料吃瓜
          </h1>
          <p className="mt-4 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            爆料吃瓜是一个专注娱乐爆料信息整理的中文内容网站。我们不做原创新闻的搬运工，而是把散落在各处的消息，按时间线整理成读者能快速看懂的进展汇总。
          </p>
        </div>
      </div>

      <section className="w-full px-4 py-12 md:px-6 md:py-16" aria-labelledby="position-title">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <span className="font-label text-xs font-medium uppercase tracking-widest text-primary-600">
              网站定位
            </span>
            <h2 id="position-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              给愿意多看一眼的人，一份更清楚的整理
            </h2>
          </div>
          <div className="space-y-5 lg:col-span-2">
            <p className="font-body text-sm leading-7 text-foreground-700 md:text-base">
              娱乐话题的特点是信息量大、更新快、真假混杂。一条短视频、一段聊天记录，往往在几小时内就被解读出十几个版本。我们想做的，是把这些碎片按时间顺序拼起来，标清楚哪些是当事人或官方已经说明的，哪些还只是网友推测。
            </p>
            <p className="font-body text-sm leading-7 text-foreground-700 md:text-base">
              网站的读者大多是喜欢围观热点、但又不希望被牵着走的普通网民。因此我们把“可回溯”放在第一位：每篇文章都保留完整的进展时间线，你随时可以回到任意一个节点，复盘事情是怎么一步步走到今天的。
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <i className="ri-time-line text-2xl text-primary-500"></i>
                <p className="mt-3 font-heading text-base font-semibold text-foreground-950">每日更新</p>
                <p className="mt-1 font-body text-sm text-foreground-600">热点事件当日补充新节点</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <i className="ri-node-tree text-2xl text-accent-600"></i>
                <p className="mt-3 font-heading text-base font-semibold text-foreground-950">时间线结构</p>
                <p className="mt-1 font-body text-sm text-foreground-600">按节点排列，方便复盘</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <i className="ri-lock-2-line text-2xl text-secondary-600"></i>
                <p className="mt-3 font-heading text-base font-semibold text-foreground-950">线索匿名</p>
                <p className="mt-1 font-body text-sm text-foreground-600">来源信息全程保密处理</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100" aria-labelledby="principle-title">
        <div className="w-full px-4 py-12 md:px-6 md:py-16">
          <span className="font-label text-xs font-medium uppercase tracking-widest text-primary-600">
            内容原则
          </span>
          <h2 id="principle-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            四条我们一直在守的规矩
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((item, index) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-2xl`}></i>
                  </span>
                  <span className="font-heading text-2xl font-bold text-background-300">
                    0{index + 1}
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-6 md:py-16" aria-labelledby="path-title">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="font-label text-xs font-medium uppercase tracking-widest text-primary-600">
              发展理念
            </span>
            <h2 id="path-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              一路走来，改了三次整理方式
            </h2>
            <ol className="mt-6 space-y-0 border-l border-background-300 pl-6">
              {milestones.map((item) => (
                <li key={item.year} className="relative pb-6 last:pb-0">
                  <span className="absolute -left-[31px] top-1 h-3 w-3 rounded-full bg-primary-500 ring-4 ring-background-50"></span>
                  <span className="font-heading text-base font-semibold text-primary-600">{item.year}</span>
                  <p className="mt-1 font-body text-sm leading-relaxed text-foreground-700">{item.text}</p>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <span className="font-label text-xs font-medium uppercase tracking-widest text-primary-600">
              覆盖话题
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              五个稳定更新的板块
            </h2>
            <div className="mt-6 space-y-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${cat.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{cat.name}</h3>
                    <p className="mt-1 font-body text-sm leading-relaxed text-foreground-600">
                      {cat.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100" aria-labelledby="voice-title">
        <div className="w-full px-4 py-12 md:px-6 md:py-16">
          <span className="font-label text-xs font-medium uppercase tracking-widest text-primary-600">
            用户口碑
          </span>
          <h2 id="voice-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            读者留下的评价
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-3">
            {testimonials.map((item) => (
              <blockquote key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                <p className="mt-3 font-body text-sm leading-relaxed text-foreground-800">{item.text}</p>
                <footer className="mt-4 border-t border-background-200 pt-3">
                  <span className="font-heading text-sm font-semibold text-foreground-950">{item.name}</span>
                  <span className="ml-3 font-label text-xs text-foreground-500">{item.role}</span>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-6 md:py-16" aria-labelledby="contact-title">
        <div className="rounded-lg border border-background-200 bg-secondary-800 p-6 md:p-10">
          <h2 id="contact-title" className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
            联系方式
          </h2>
          <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-secondary-100">
            有线索想提供、有内容需要更正，或者想聊聊合作，都可以通过下面的方式找到我们。我们通常在两个工作日内回复。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-secondary-700 p-5">
              <i className="ri-mail-line text-2xl text-accent-300"></i>
              <p className="mt-3 font-label text-xs text-secondary-200">投稿与联系邮箱</p>
              <p className="mt-1 font-heading text-base font-semibold text-background-50">
                baoliao@chigua-site.com
              </p>
            </div>
            <div className="rounded-lg bg-secondary-700 p-5">
              <i className="ri-edit-line text-2xl text-accent-300"></i>
              <p className="mt-3 font-label text-xs text-secondary-200">在线线索提交</p>
              <Link
                to="/submit"
                className="mt-1 inline-flex items-center gap-1 font-heading text-base font-semibold text-background-50 transition-colors hover:text-accent-300"
              >
                前往爆料入口
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="rounded-lg bg-secondary-700 p-5">
              <i className="ri-time-line text-2xl text-accent-300"></i>
              <p className="mt-3 font-label text-xs text-secondary-200">内容更新时段</p>
              <p className="mt-1 font-heading text-base font-semibold text-background-50">每日 09:00 - 24:00</p>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}